import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpApiService } from '@recruitee/http';
import {
  EmailTemplatesAutoReplyItem,
  EmailTemplatesAutoReplyItemTranslation,
  EmailTemplatesAutoReplyListItem,
  TemplateAssignedOffer,
} from '@recruitee/http/types';

@Injectable({ providedIn: 'root' })
export class EmailTemplatesAutoReplyHttpClient {
  constructor(protected httpApi: HttpApiService) {}

  public loadCollection(): Observable<EmailTemplatesAutoReplyListItem[]> {
    return this.httpApi.get(`/auto_reply_templates`).pipe(map(data => data.autoReplyTemplates));
  }

  public loadItem(id: number): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .get(`/auto_reply_templates/${id}`)
      .pipe(map(data => data.autoReplyTemplate));
  }

  public create(title: string): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .post(`/auto_reply_templates`, null, { autoReplyTemplate: { title } })
      .pipe(map(data => data.autoReplyTemplate));
  }

  public update(
    templateData: Partial<EmailTemplatesAutoReplyItem>,
  ): Observable<EmailTemplatesAutoReplyItem> {
    const { id, ...autoReplyTemplate } = templateData;

    return this.httpApi
      .patch(`/auto_reply_templates/${id}`, null, { autoReplyTemplate })
      .pipe(map(data => data.autoReplyTemplate));
  }

  public duplicate(id: number): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .post(`/auto_reply_templates/${id}`, null, null)
      .pipe(map(data => data.autoReplyTemplate));
  }

  public delete(id: number): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .delete(`/auto_reply_templates/${id}`, null)
      .pipe(map(data => data.autoReplyTemplate));
  }

  public setDefault(id: number): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .patch(`/auto_reply_templates/${id}/set_default`, null, {
        autoReplyTemplate: {
          default: true,
        },
      })
      .pipe(map(data => data.autoReplyTemplate));
  }

  public assignedOffersList(
    id: number,
    searchParams: { q: string | null; page: number; limit: number },
  ): Observable<{
    assignedOffers: TemplateAssignedOffer[];
    meta: { limit: number; page: number; totalCount: number };
  }> {
    return this.httpApi.get(`auto_reply_templates/${id}/assigned_offers`, searchParams);
  }

  public loadOfferTemplates(
    offerId: number | string,
  ): Observable<EmailTemplatesAutoReplyListItem[]> {
    return this.httpApi
      .get(`/offers/${offerId}/auto_reply_templates`)
      .pipe(map(data => data.autoReplyTemplates));
  }

  public loadOfferTemplateItem(
    offerId: number | string,
    templateId: number,
  ): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .get(`offers/${offerId}/auto_reply_templates/${templateId}`)
      .pipe(map(data => data.autoReplyTemplate));
  }

  public assignTemplateToOffer(
    offerId: number | string,
    autoReplyTemplateId: number,
  ): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .patch(`offers/${offerId}/auto_reply_templates/assign`, null, {
        autoReplyTemplateId,
      })
      .pipe(map(data => data.autoReplyTemplate));
  }

  public createOrUpdateCustomTemplate(
    offerId: number | string,
    translation: { bodyHtml: string; subject: string; langCode: string },
    templateAttachmentAssignments: { attachmentId: number }[],
    sourceTemplateId: number | null,
    isCustomAssigned: boolean,
  ): Observable<EmailTemplatesAutoReplyItem> {
    const body = {
      autoReplyTemplate: { translations: [translation], templateAttachmentAssignments },
      sourceTemplateId,
    };

    const url = `/offers/${offerId}/auto_reply_templates/custom`;

    const observable = isCustomAssigned
      ? this.httpApi.patch(url, null, body)
      : this.httpApi.post(url, null, body);

    return observable.pipe(map(data => data.autoReplyTemplate));
  }

  public saveAsTemplate(
    title: string,
    offerId: number | string,
    translations: EmailTemplatesAutoReplyItemTranslation[],
  ): Observable<EmailTemplatesAutoReplyItem> {
    return this.httpApi
      .post(`/offers/${offerId}/auto_reply_templates/save_as_template`, null, {
        autoReplyTemplate: { title, translations },
      })
      .pipe(map(data => data.autoReplyTemplate));
  }

  public updatePositions(ids: number[]): Observable<EmailTemplatesAutoReplyListItem[]> {
    return this.httpApi
      .patch(`/auto_reply_templates/update_positions`, null, {
        autoReplyTemplates: ids,
      })
      .pipe(map(data => data.autoReplyTemplates));
  }
}
